import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import { getAutoLogoutConfig } from '../config/autoLogout'

export default function TestAutoLogoutPage() {
  const { user, isAuthenticated } = useAuthStore()
  const [activityCount, setActivityCount] = useState(0)
  const [lastActivity, setLastActivity] = useState<Date>(new Date())
  const config = getAutoLogoutConfig()

  useEffect(() => {
    const handleActivity = () => {
      setActivityCount(prev => prev + 1)
      setLastActivity(new Date())
    }

    // Listen for activity events
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown']
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true })
    })

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
    }
  }, [])

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <h1 className="text-2xl font-bold text-center mb-4">Auto-Logout Test</h1>
          <p className="text-gray-600 text-center">
            Please log in to test the auto-logout functionality.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-3xl font-bold mb-6">Auto-Logout Test Page</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* User Info */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold mb-3">Current User</h2>
              <div className="space-y-2">
                <p><strong>Email:</strong> {user?.email}</p>
                <p><strong>Role:</strong> {user?.role}</p>
                <p><strong>Authenticated:</strong> {isAuthenticated ? '✅ Yes' : '❌ No'}</p>
              </div>
            </div>

            {/* Auto-Logout Config */}
            <div className="bg-amber-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold mb-3">Auto-Logout Settings</h2>
              <div className="space-y-2">
                <p><strong>Timeout:</strong> {config.timeoutMinutes} minutes</p>
                <p><strong>Warning:</strong> {config.warningMinutes} minutes before</p>
                <p><strong>Enabled:</strong> {config.enabled ? '✅ Yes' : '❌ No'}</p>
                <p><strong>Environment:</strong> {import.meta.env.DEV ? 'Development' : 'Production'}</p>
                <p><strong>VITE_AUTO_LOGOUT_ENABLED:</strong> {import.meta.env.VITE_AUTO_LOGOUT_ENABLED || 'Not Set'}</p>
                <p><strong>VITE_AUTO_LOGOUT_TIMEOUT_MINUTES:</strong> {import.meta.env.VITE_AUTO_LOGOUT_TIMEOUT_MINUTES || 'Not Set'}</p>
                <p><strong>VITE_AUTO_LOGOUT_WARNING_MINUTES:</strong> {import.meta.env.VITE_AUTO_LOGOUT_WARNING_MINUTES || 'Not Set'}</p>
              </div>
            </div>

            {/* Activity Tracker */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold mb-3">Activity Tracker</h2>
              <div className="space-y-2">
                <p><strong>Activity Count:</strong> {activityCount}</p>
                <p><strong>Last Activity:</strong> {lastActivity.toLocaleTimeString()}</p>
              </div>
            </div>

            {/* Test Instructions */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold mb-3">Test Instructions</h2>
              <div className="space-y-2 text-sm">
                <p>1. Stay inactive for {config.timeoutMinutes - config.warningMinutes} minutes</p>
                <p>2. A warning modal should appear</p>
                <p>3. If no action is taken, you'll be logged out after {config.warningMinutes} minute(s)</p>
                <p>4. Any mouse movement, click, or key press resets the timer</p>
              </div>
            </div>
          </div>

          {/* Test Actions */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-lg font-semibold mb-3">Test Actions</h2>
            <div className="space-x-4">
              <button
                onClick={() => {
                  setActivityCount(prev => prev + 1)
                  setLastActivity(new Date())
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Trigger Activity
              </button>
              
              <button
                onClick={() => {
                  setActivityCount(0)
                  setLastActivity(new Date())
                }}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Reset Counter
              </button>
            </div>
          </div>

          {/* Environment Variables Debug */}
          <div className="mt-6 p-4 bg-gray-100 border border-gray-300 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-3">Environment Variables Debug</h3>
            <div className="bg-white p-3 rounded border text-xs font-mono overflow-x-auto">
              <pre>{JSON.stringify({
                VITE_AUTO_LOGOUT_ENABLED: import.meta.env.VITE_AUTO_LOGOUT_ENABLED,
                VITE_AUTO_LOGOUT_TIMEOUT_MINUTES: import.meta.env.VITE_AUTO_LOGOUT_TIMEOUT_MINUTES,
                VITE_AUTO_LOGOUT_WARNING_MINUTES: import.meta.env.VITE_AUTO_LOGOUT_WARNING_MINUTES,
                VITE_DEV_AUTO_LOGOUT_TIMEOUT_MINUTES: import.meta.env.VITE_DEV_AUTO_LOGOUT_TIMEOUT_MINUTES,
                VITE_DEV_AUTO_LOGOUT_WARNING_MINUTES: import.meta.env.VITE_DEV_AUTO_LOGOUT_WARNING_MINUTES,
                NODE_ENV: import.meta.env.NODE_ENV,
                DEV: import.meta.env.DEV,
                PROD: import.meta.env.PROD,
                MODE: import.meta.env.MODE
              }, null, 2)}</pre>
            </div>
          </div>

          {/* Development Mode Notice */}
          {import.meta.env.DEV && (
            <div className="mt-6 p-4 bg-yellow-100 border border-yellow-400 rounded-lg">
              <h3 className="font-semibold text-yellow-800">Development Mode</h3>
              <p className="text-yellow-700 text-sm mt-1">
                Auto-logout is configured with shorter timeouts for testing purposes.
                In production, the timeout will be {parseInt(import.meta.env.VITE_AUTO_LOGOUT_TIMEOUT_MINUTES || '15')} minutes.
              </p>
            </div>
          )}

          {/* Production Mode Notice */}
          {!import.meta.env.DEV && (
            <div className="mt-6 p-4 bg-blue-100 border border-blue-400 rounded-lg">
              <h3 className="font-semibold text-blue-800">Production Mode</h3>
              <p className="text-blue-700 text-sm mt-1">
                You are currently testing in PRODUCTION mode. Auto-logout will use the production timeout of {config.timeoutMinutes} minutes.
                Check the browser console for debug logs.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 